import React, { useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { inputParametersHierarchyApi } from '../../../store/ai/inputParametersHierarchy'
import { selectAccountId } from '../../../store/auth/authSlice'
import { User } from '../../../models/User'
import { useCloneTemplateMutation, useDeleteTemplateMutation, useListTemplatesQuery } from '../../../store/templateLibrary/templateLibrary'
import { CustomTemplateValue, TemplateCloneRequest, TemplateLookup, TemplateTabContent } from '../../../models/TemplateLibrary'
import { Dropdown, Form, Spinner } from 'react-bootstrap'
import { TemplateTypeBadge } from './TemplateTypeBadge'
import { TemplateScopeBadge } from './TemplateScopeBadge'
import { SystemUserBadge, TemplateCreatedByUserBadge } from './TemplateCreatedByUserBadge'
import { formatDate } from '../../../common/dateUtils'
import { ADMIN_EDITABLE_TEMPLATE_SCOPES, ZOCKS_SYSTEM_USER_ID } from './utils'
import NoTemplatesYetIllustration from '../../../assets/images/no-templates-yet.png?url'
import { TemplateDuplicateModal } from './TemplateDuplicateModal'
import { useTemplateCreators } from './useTemplateCreators'
import { TemplateDeleteConfirmationModal } from './TemplateDeleteConfirmationModal'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { useAccount } from '../../../common/useAccount'
import { useNavigate } from 'react-router-dom'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getTemplateLibraryTrackingDetails } from './tracking'
import { isSchedulingEmailTemplate } from './TemplatesTab'
import './TemplatesTabView.scss'

export interface TemplatesTabViewProps {
  templatesTab: TemplateTabContent
  selectedTemplateId?: string
  onClick?: (templateLookup: TemplateLookup) => void
}

export const TemplatesTabView: React.FC<TemplatesTabViewProps> = ({ templatesTab, selectedTemplateId, onClick }) => {
  const accountId = useAppSelector(selectAccountId)
  const [templateFilter, setTemplateFilter] = useState<string>('')
  const { data: templates, isLoading: isTemplatesLoading } = useListTemplatesQuery(
    { accountId, ...templatesTab.searchParams },
    { refetchOnMountOrArgChange: 60 },
  )
  const usersById = useTemplateCreators(templates?.data)

  const handleTemplateFilterChange = async (filter: string) => {
    setTemplateFilter(filter)
  }

  const templateFilterFunction = (template: TemplateLookup) => {
    if (!templateFilter) {
      return true
    }
    return template.name.toLowerCase().includes(templateFilter.toLowerCase()) || template.description.toLowerCase().includes(templateFilter.toLowerCase())
  }

  const filteredTemplates = templates?.data.filter(templateFilterFunction) ?? []

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center gap-3'>
        <div>
          <h4 className='mb-0'>{templatesTab.header}</h4>
          <div className='paragraph-medium'>{templatesTab.subHeader}</div>
        </div>
        <div className='template-search-control d-flex align-items-center gap-2 border rounded zocks-border-color-gray-500 px-2 flex-grow-1'>
          <i className='bi bi-search label-large' />
          <Form.Control
            type='text'
            list='rooms'
            value={templateFilter}
            onChange={(e) => handleTemplateFilterChange(e.currentTarget.value)}
            placeholder={`Search ${templatesTab.label}`}
            className='border-0 px-0'
          />
        </div>
      </div>
      {isTemplatesLoading && (
        <div className='d-flex justify-content-center align-items-center h-100 pt-5'>
          <Spinner />
        </div>
      )}
      {templates && filteredTemplates.length === 0 ? <TemplatesEmptyState /> : null}
      {templates && filteredTemplates.length > 0 && (
        <div className='templates-list row row-cols-1 row-cols-md-2 g-3 mt-3'>
          {filteredTemplates.map((template) => (
            <TemplateView
              key={template.id}
              templateLookup={template}
              showTemplateType={templatesTab.showTemplateType}
              createdByUser={'createdBy' in template ? usersById[(template as CustomTemplateValue).createdBy] : undefined}
              selected={template.id === selectedTemplateId}
              onClick={onClick}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const TemplatesEmptyState: React.FC = () => (
  <div className='templates-empty-state d-flex flex-column align-items-center text-center py-5 mx-auto'>
    <img src={NoTemplatesYetIllustration} alt='' aria-hidden='true' width={240} className='mb-3 h-auto' />
    <h4 className='templates-empty-state__title fw-bold zocks-text-gray-900 mb-1'>No templates yet</h4>
    <p className='zocks-text-gray-700 mb-0'>Create a new template or open any existing one to duplicate it.</p>
  </div>
)

interface TemplateViewProps {
  templateLookup: TemplateLookup
  showTemplateType: boolean
  createdByUser?: User
  selected?: boolean
  onClick?: (templateLookup: TemplateLookup) => void
}

export const TemplateView: React.FC<TemplateViewProps> = ({ templateLookup, showTemplateType, createdByUser, selected = false, onClick }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { accountId, account } = useAccount()
  const { hasScope } = useZocksAuth()
  const hasTemplateAdminScope = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])
  const [cloneTemplate, { isLoading: isCloning }] = useCloneTemplateMutation()
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteTemplateMutation()
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const { tracked } = useTracking()

  const templatesLocked = account?.enterpriseConfig.lockTemplates ?? false
  const isAccountOnly = isSchedulingEmailTemplate(templateLookup)
  const adminOnlyAllowed = !isAccountOnly || hasTemplateAdminScope
  const canDuplicate = !templatesLocked && adminOnlyAllowed
  const canDelete =
    !templatesLocked &&
    adminOnlyAllowed &&
    (templateLookup.scope === 'user' || (hasTemplateAdminScope && ADMIN_EDITABLE_TEMPLATE_SCOPES.has(templateLookup.scope)))
  const showMenu = canDuplicate || canDelete

  const handleTemplateClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.target as Node)) return
    if ((e.target as HTMLElement).closest('.template-actions-dropdown')) return
    onClick?.(templateLookup)
  }

  const handleDuplicate = async (request: TemplateCloneRequest) => {
    const clone = await cloneTemplate({ accountId, templateId: templateLookup.id, request }).unwrap()
    setShowDuplicateModal(false)
    navigate(`/workspace/setup/templates/template/${clone.id}`, { replace: true })
  }

  const _handleDelete = async (replaceWithTemplateId?: string | null) => {
    await deleteTemplate({ accountId, templateId: templateLookup.id, replaceWithTemplateId }).unwrap()
    dispatch(inputParametersHierarchyApi.util.invalidateTags(['InputParametersHierarchy']))
    setShowDeleteModal(false)
  }
  const handleDelete = tracked(
    _handleDelete,
    { eventName: TrackEvent.TEMPLATE_DELETED },
    {
      eventDetailsExtractor: (replaceWithTemplateId) => ({
        ...getTemplateLibraryTrackingDetails(templateLookup),
        hasReplacement: replaceWithTemplateId != null,
      }),
    },
  )

  return (
    <div key={templateLookup.id} className='col'>
      <div
        className={`d-flex flex-column template-item ${selected ? 'template-item-selected' : ''} p-3 h-100 cursor-pointer`}
        onClick={handleTemplateClick}
        role='button'
        tabIndex={0}
      >
        <div className='d-flex justify-content-between align-items-start gap-2 pb-1 paragraph-small'>
          <div className='d-flex align-items-center gap-2'>
            {showTemplateType ? <TemplateTypeBadge templateValue={templateLookup} /> : null}
            <TemplateScopeBadge scope={templateLookup.scope} />
          </div>
          {showMenu ? (
            <Dropdown align='end' className='template-actions-dropdown'>
              <Dropdown.Toggle as={TemplateActionsToggle} aria-label='Template actions' />
              <Dropdown.Menu className='template-actions-menu'>
                {canDuplicate ? (
                  <Dropdown.Item onClick={() => setShowDuplicateModal(true)}>
                    <i className='bi bi-copy' /> Duplicate
                  </Dropdown.Item>
                ) : null}
                {canDelete ? (
                  <Dropdown.Item onClick={() => setShowDeleteModal(true)}>
                    <i className='bi bi-trash' /> Delete
                  </Dropdown.Item>
                ) : null}
              </Dropdown.Menu>
            </Dropdown>
          ) : null}
        </div>
        <h6 className='template-name paragraph-large fw-bold pt-1'>{templateLookup.name}</h6>
        <p className='paragraph-small zocks-text-gray-700 pt-2'>{templateLookup.description}</p>
        <div className='d-flex justify-content-start align-items-center gap-3 paragraph-extra-small mt-auto'>
          {createdByUser ? (
            <TemplateCreatedByUserBadge createdByUser={createdByUser} />
          ) : templateLookup.scope === 'system' || ('createdBy' in templateLookup && templateLookup.createdBy === ZOCKS_SYSTEM_USER_ID) ? (
            <SystemUserBadge />
          ) : null}
          {'updatedAt' in templateLookup ? (
            <div className='d-flex align-items-center gap-1'>
              <i className='bi bi-calendar' />
              <p className='mb-0'>Updated {formatDate(new Date(templateLookup.updatedAt))}</p>
            </div>
          ) : null}
        </div>
      </div>
      {showDuplicateModal ? (
        <TemplateDuplicateModal
          show={showDuplicateModal}
          loading={isCloning}
          templateValue={templateLookup}
          isAccountOnly={isAccountOnly}
          onDuplicate={handleDuplicate}
          onCancel={() => setShowDuplicateModal(false)}
        />
      ) : null}
      {showDeleteModal ? (
        <TemplateDeleteConfirmationModal
          show={showDeleteModal}
          loading={isDeleting}
          templateValue={templateLookup}
          onDelete={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      ) : null}
    </div>
  )
}

const TemplateActionsToggle = React.forwardRef<HTMLButtonElement, { onClick?: (e: React.MouseEvent) => void; 'aria-label'?: string }>(
  ({ onClick, 'aria-label': ariaLabel }, ref) => (
    <button
      ref={ref}
      type='button'
      className='template-actions-toggle d-flex align-items-center justify-content-center'
      aria-label={ariaLabel}
      onClick={onClick}
    >
      <i className='bi bi-three-dots' />
    </button>
  ),
)
